import './global.css';
import {Routes, Route, Outlet, Navigate, useNavigate, useLocation} from "react-router-dom";
import {useEffect, useState} from "react";
import {connect, useDispatch} from "react-redux";

import {useTranslation} from "react-i18next";
import usePreviousLocation from "./hooks/track-route-hook";
import PageScroll from "./components/page_scroll/PageScroll";
import {clearData} from "./utils/clearData";

import RegisterLoader from "./components/registration/register/LazyRegister";
import AboutLanding from "./components/about_us/about_info_landing/AboutLanding";
import AccountLanding from "./components/account/account_info_landing/AccountLanding";
import IraAccLanding from "./components/ira_acc/ira_account_landing/IraAccLanding";
import PersonalLanding from "./components/personal/personal_info_landing/PersonalLanding";
import FinancialLanding from "./components/financial/financial_landing/FinancialLanding";
import AffiliationLanding from "./components/affiliation/affiliation_info_landing/AffiliationLanding";
import ExperienceLanding from "./components/experience/experience_info_landing/ExperienceLanding";
import ConditionsLanding from "./components/conditions/conditions_landing/ConditionsLanding";
import ReviewLanding from "./components/review/review_landing/ReviewLanding";
import SignatureLanding from "./components/signature/signature_landing/SignatureLanding";
import WelcomeToLanding from "./components/welcome/welcome_to_landing/WelcomeToLanding";
import WelcomeBackInfo from "./components/welcome-back/welcome_back_info/WelcomeBackInfo";
import Login from "./components/login/login_info/Login";
import CustodialAccLanding from "./components/custodial_acc/custodial_account_landing/CustodialAccLanding";
import OwnerInfoLanding from "./components/owner_info/owner_info_landing/OwnerInfoLanding";
import AuthorizedSignersLanding
    from "./components/authorized_signers/authorized_signers_landing/AuthorizedSignersLanding";
import VerifyPasswordLanding from "./components/verify_password/verify_password_landing/VerifyPasswordLanding";

import {setAppData} from "./redux/register_store";
import {setLoginInfo} from "./redux/login_store";
import {setPersonalData} from "./redux/personal_store";
import {setFinancialInfo} from "./redux/financial_store";
import {setAffiliationField} from "./redux/affiliation_store";
import {setExpInfoData} from "./redux/experience_store";
import {setConditionInfo} from "./redux/conditions_store";
import {setCustodialData} from "./redux/custodial_acc_store";
import {setOwnerData} from "./redux/owner_info_store";
import {setAuthInfo} from "./redux/auth_singer_store";
import {setVerifyInfo} from "./redux/verify_password_store";
import {setLanguage} from './redux/actions_store';

function App({appData}) {
    const [response, setResponse] = useState();
    const {i18n} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const prevLocation = usePreviousLocation();

    clearData({
        setAppData,
        setPersonalData,
        setFinancialInfo,
        setAffiliationField,
        setExpInfoData,
        setConditionInfo,
        setLoginInfo,
        setCustodialData,
        setOwnerData,
        setAuthInfo,
        setVerifyInfo
    });

    const updateResponse = (data) => setResponse(data);

    const userLocationHistoryManager = () => {
        let history = localStorage.getItem('history');

        if (!history) {
            history = [location.pathname];
        } else {
            history = JSON.parse(history);
            if (history.at(-1) !== location.pathname) {
                history.push(location.pathname);
            }
        }

        localStorage.setItem('history', JSON.stringify(history));

        return history;
    };

    useEffect(() => {
        const history = userLocationHistoryManager();

        if (location.pathname === '/welcome-to' && !prevLocation) {
            clearData({
                setAppData,
                setPersonalData,
                setFinancialInfo,
                setAffiliationField,
                setExpInfoData,
                setConditionInfo,
                setLoginInfo,
                setCustodialData,
                setOwnerData,
                setAuthInfo,
                setVerifyInfo
            });
            navigate('/login');
        } else if (history.at(-2) === '/welcome-to') {
            clearData({
                setAppData,
                setPersonalData,
                setFinancialInfo,
                setAffiliationField,
                setExpInfoData,
                setConditionInfo,
                setLoginInfo,
                setCustodialData,
                setOwnerData,
                setAuthInfo,
                setVerifyInfo
            });
            navigate('/login');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevLocation, navigate]);

    const ProtectedRoute = ({redirectPath = '/'}) => {
        if (!appData.app_id) {
            return <Navigate to={redirectPath} replace/>;
        }
        return <Outlet/>;
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const lang = params.get('lang');
        if (lang) {
            dispatch(setLanguage(lang));
            i18n.changeLanguage(lang);
            window.localStorage.setItem('Localelanguage', lang);
        }
    }, [dispatch, i18n]);

    return (
        <div className="App">
            <PageScroll>
                <Routes>
                    <Route path="/" element={<RegisterLoader/>}/>
                    <Route path="/about-us" element={<AboutLanding/>}/>

                    <Route element={<ProtectedRoute/>}>
                        <Route path="personal" element={<PersonalLanding/>}/>
                        <Route path="financial" element={<FinancialLanding/>}/>
                        <Route path="affiliation" element={<AffiliationLanding/>}/>
                        <Route path="account" element={<AccountLanding/>}/>
                        <Route path="experience" element={<ExperienceLanding/>}/>
                        <Route path="conditions" element={<ConditionsLanding/>}/>
                        <Route path="review" element={<ReviewLanding/>}/>
                        <Route path="signature" element={<SignatureLanding/>}/>
                        <Route path="welcome-to" element={<WelcomeToLanding/>}/>
                        <Route path="custodial-account" element={<CustodialAccLanding/>}/>
                        <Route path="ira-account" element={<IraAccLanding/>}/>
                        <Route path="owner-info" element={<OwnerInfoLanding/>}/>
                        <Route path="authorized-signers" element={<AuthorizedSignersLanding/>}/>
                    </Route>
                    <Route path="welcome-back" element={<WelcomeBackInfo response={response}/>}/>
                    <Route path="login" element={<Login updateResponse={updateResponse}/>}/>
                    <Route path="verify-password" element={<VerifyPasswordLanding/>}/>
                </Routes>
            </PageScroll>
        </div>
    );
}

const mapStateToProps = (state) => ({
    appData: state.appData,
});

const mapDispatchToProps = {
    setLoginInfo,
    setAppData,
    setPersonalData,
    setFinancialInfo,
    setAffiliationField,
    setExpInfoData,
    setConditionInfo,
    setCustodialData,
    setOwnerData,
    setAuthInfo,
    setVerifyInfo
};

export default connect(mapStateToProps, mapDispatchToProps)(App);